import React, { useState, useMemo } from 'react';

const HierarchyTable = ({ data, viewMode: initialViewMode = 'location' }) => {
  // State for view mode (location or product)
  const [viewMode, setViewMode] = useState(initialViewMode);
  
  // Track expanded items in each level
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedSubRegions, setExpandedSubRegions] = useState({});
  const [expandedCities, setExpandedCities] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  
  // Group data hierarchically
  const hierarchyData = useMemo(() => {
    // Create two separate hierarchy structures
    const result = {
      // For location-based hierarchy (Google locations)
      locationHierarchy: {},
      
      // For product-based hierarchy (Amazon products and apps)
      productHierarchy: {
        'Amazon Products': {
          total: 0,
          positive: 0,
          negative: 0,
          neutral: 0,
          products: {}
        },
        'Mobile Apps': {
          total: 0,
          positive: 0,
          negative: 0,
          neutral: 0,
          apps: {}
        }
      },
      
      // Summary counts
      totalLocationReviews: 0,
      totalProductReviews: 0
    };
    
    // Helper function to extract main region from sub-region
    const getMainRegion = (region) => {
      // If it's a main region (without a number), return itself
      if (!region.match(/\d+$/)) {
        return region;
      }
      
      // Otherwise, extract the main region part (e.g., "North 1" -> "North")
      return region.replace(/\s+\d+$/, '');
    };
    
    data.forEach(review => {
      const { region, city, source, sentiment } = review;
      
      if (source === 'Google Reviews') {
        // Location-based (Google reviews)
        result.totalLocationReviews += 1;
        
        // Extract main region and determine if this is a sub-region
        const mainRegion = getMainRegion(region);
        const isSubRegion = mainRegion !== region;
        
        // Main region level (if it doesn't exist yet)
        if (!result.locationHierarchy[mainRegion]) {
          result.locationHierarchy[mainRegion] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0,
            subRegions: {}
          };
        }
        
        // Sub-region level (either the real sub-region or a placeholder for the main region)
        const subRegionKey = isSubRegion ? region : `${mainRegion} Main`;
        if (!result.locationHierarchy[mainRegion].subRegions[subRegionKey]) {
          result.locationHierarchy[mainRegion].subRegions[subRegionKey] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0,
            cities: {}
          };
        }
        
        // City level
        if (!result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city]) {
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0,
            locations: {}
          };
        }
        
        // Store/Location level (use city name + a number as placeholder)
        const locationName = `${city} Store ${Math.floor(Math.random() * 3) + 1}`;
        if (!result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].locations[locationName]) {
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].locations[locationName] = {
            total: 0,
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        
        // Increment counters at all levels
        result.locationHierarchy[mainRegion].total += 1;
        result.locationHierarchy[mainRegion].subRegions[subRegionKey].total += 1;
        result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].total += 1;
        result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].locations[locationName].total += 1;
        
        // Track sentiment at all levels
        if (sentiment === 'Positive') {
          result.locationHierarchy[mainRegion].positive += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].positive += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].positive += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].locations[locationName].positive += 1;
        } else if (sentiment === 'Negative') {
          result.locationHierarchy[mainRegion].negative += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].negative += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].negative += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].locations[locationName].negative += 1;
        } else {
          result.locationHierarchy[mainRegion].neutral += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].neutral += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].neutral += 1;
          result.locationHierarchy[mainRegion].subRegions[subRegionKey].cities[city].locations[locationName].neutral += 1;
        }
      } else {
        // Product-based (Amazon, App Store, Play Store)
        result.totalProductReviews += 1;
        
        let categoryName, entityKey, entityList;
        
        if (source === 'Amazon') {
          categoryName = 'Amazon Products';
          entityKey = 'products';
          
          // For Amazon, create product categories
          const productTypes = [
            'Water Purifier - RO',
            'Water Purifier - UV',
            'Water Purifier - Gravity',
            'Water Filter - Pitcher',
            'Water Filter - Faucet'
          ];
          
          // Assign a consistent product type based on review ID
          entityList = productTypes[review.id % productTypes.length];
        } else {
          categoryName = 'Mobile Apps';
          entityKey = 'apps';
          entityList = source; // App Store or Play Store
        }
        
        // Increment category totals
        result.productHierarchy[categoryName].total += 1;
        
        // Create entity if it doesn't exist
        if (!result.productHierarchy[categoryName][entityKey][entityList]) {
          result.productHierarchy[categoryName][entityKey][entityList] = {
            total: 0, 
            positive: 0,
            negative: 0,
            neutral: 0
          };
        }
        
        // Increment entity totals
        result.productHierarchy[categoryName][entityKey][entityList].total += 1;
        
        // Track sentiment
        if (sentiment === 'Positive') {
          result.productHierarchy[categoryName].positive += 1;
          result.productHierarchy[categoryName][entityKey][entityList].positive += 1;
        } else if (sentiment === 'Negative') {
          result.productHierarchy[categoryName].negative += 1;
          result.productHierarchy[categoryName][entityKey][entityList].negative += 1;
        } else {
          result.productHierarchy[categoryName].neutral += 1;
          result.productHierarchy[categoryName][entityKey][entityList].neutral += 1;
        }
      }
    });
    
    return result;
  }, [data]);
  
  // Toggle region expansion
  const toggleRegion = (region) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };
  
  // Toggle sub-region expansion
  const toggleSubRegion = (region, subRegion) => {
    const key = `${region}_${subRegion}`;
    setExpandedSubRegions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Toggle city expansion
  const toggleCity = (region, subRegion, city) => {
    const key = `${region}_${subRegion}_${city}`;
    setExpandedCities(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Toggle product category expansion
  const toggleProductCategory = (category) => {
    setExpandedProducts(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Calculate percentage
  const getPercentage = (part, total) => {
    return total > 0 ? Math.round((part / total) * 100) : 0;
  };
  
  // Return visible class based on expansion state
  const getSubRegionRowClass = (region) => {
    return expandedRegions[region] ? '' : 'hidden';
  };
  
  const getCityRowClass = (region, subRegion) => {
    const key = `${region}_${subRegion}`;
    return expandedRegions[region] && expandedSubRegions[key] ? '' : 'hidden';
  };
  
  const getLocationRowClass = (region, subRegion, city) => {
    const regionKey = `${region}_${subRegion}`;
    const cityKey = `${region}_${subRegion}_${city}`;
    return expandedRegions[region] && expandedSubRegions[regionKey] && expandedCities[cityKey] ? '' : 'hidden';
  };
  
  const getProductRowClass = (category) => {
    return expandedProducts[category] ? '' : 'hidden';
  };
  
  // Get background color for row based on level
  const getRowBgColor = (level) => {
    switch (level) {
      case 'region':
      case 'category': return 'hover:bg-blue-50';
      case 'subregion': return 'bg-gray-50 hover:bg-blue-50';
      case 'city':
      case 'product': return 'bg-gray-100 hover:bg-blue-50';
      case 'location': return 'bg-gray-200 hover:bg-blue-50';
      default: return '';
    }
  };
  
  // Get indent width based on level
  const getIndent = (level) => {
    switch (level) {
      case 'region':
      case 'category': return 'ml-0';
      case 'subregion': return 'ml-6';
      case 'city':
      case 'product': return 'ml-12';
      case 'location': return 'ml-18';
      default: return 'ml-0';
    }
  };
  
  // Get chevron icon based on expansion state
  const getChevronIcon = (expanded) => {
    return expanded ? (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    ) : (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
      </svg>
    );
  };
  
  // Get icon for entity type
  const getEntityIcon = (type) => {
    if (type === 'location') {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"></path>
        </svg>
      );
    } else if (type === 'product') {
      return (
        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 5H5c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 12H5V7h14v10z"></path>
          <path d="M7 9h10v2H7z"></path>
          <path d="M7 12h7v2H7z"></path>
        </svg>
      );
    } else if (type === 'app') {
      return (
        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"></path>
        </svg>
      );
    }
    return null;
  };
  
  return (
    <div>
      {/* View mode toggle if both location and product data exist */}
      {hierarchyData.totalLocationReviews > 0 && hierarchyData.totalProductReviews > 0 && (
        <div className="flex mb-4">
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                viewMode === 'location'
                  ? 'bg-blue-50 text-blue-700 border border-blue-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setViewMode('location')}
            >
              Location
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium rounded-r-md ${
                viewMode === 'product'
                  ? 'bg-blue-50 text-blue-700 border border-blue-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setViewMode('product')}
            >
              Product
            </button>
          </div>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Reviews
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Positive
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Negative
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Neutral
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sentiment
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Location-based hierarchy (Google Reviews) */}
            {viewMode === 'location' && Object.entries(hierarchyData.locationHierarchy).map(([region, regionData]) => (
              <React.Fragment key={`region-${region}`}>
                {/* Region row */}
                <tr 
                  className={`cursor-pointer ${getRowBgColor('region')}`} 
                  onClick={() => toggleRegion(region)}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                    <span className="mr-2">{getChevronIcon(expandedRegions[region])}</span>
                    <span className="font-bold">{region}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {regionData.total}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {regionData.positive} ({getPercentage(regionData.positive, regionData.total)}%)
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {regionData.negative} ({getPercentage(regionData.negative, regionData.total)}%)
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {regionData.neutral} ({getPercentage(regionData.neutral, regionData.total)}%)
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                           style={{ width: `${getPercentage(regionData.positive, regionData.total)}%` }}>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Sub-region rows */}
                {Object.entries(regionData.subRegions).map(([subRegion, subRegionData]) => (
                  <React.Fragment key={`subregion-${region}-${subRegion}`}>
                    {/* Sub-region row */}
                    <tr 
                      className={`cursor-pointer ${getRowBgColor('subregion')} ${getSubRegionRowClass(region)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSubRegion(region, subRegion);
                      }}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                        <div className="w-6"></div>
                        <span className="mr-2">{getChevronIcon(expandedSubRegions[`${region}_${subRegion}`])}</span>
                        <span>{subRegion}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                        {subRegionData.total}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {subRegionData.positive} ({getPercentage(subRegionData.positive, subRegionData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {subRegionData.negative} ({getPercentage(subRegionData.negative, subRegionData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {subRegionData.neutral} ({getPercentage(subRegionData.neutral, subRegionData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                               style={{ width: `${getPercentage(subRegionData.positive, subRegionData.total)}%` }}>
                          </div>
                        </div>
                      </td>
                    </tr>
                    
                    {/* City rows */}
                    {Object.entries(subRegionData.cities).map(([city, cityData]) => (
                      <React.Fragment key={`city-${region}-${subRegion}-${city}`}>
                        {/* City row */}
                        <tr 
                          className={`cursor-pointer ${getRowBgColor('city')} ${getCityRowClass(region, subRegion)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCity(region, subRegion, city);
                          }}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                            <div className="w-12"></div>
                            <span className="mr-2">{getChevronIcon(expandedCities[`${region}_${subRegion}_${city}`])}</span>
                            <span>{city}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {cityData.total}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {cityData.positive} ({getPercentage(cityData.positive, cityData.total)}%)
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {cityData.negative} ({getPercentage(cityData.negative, cityData.total)}%)
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {cityData.neutral} ({getPercentage(cityData.neutral, cityData.total)}%)
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                                  style={{ width: `${getPercentage(cityData.positive, cityData.total)}%` }}>
                              </div>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Location rows */}
                        {Object.entries(cityData.locations).map(([location, locationData]) => (
                          <tr 
                            key={`location-${region}-${subRegion}-${city}-${location}`}
                            className={`${getRowBgColor('location')} ${getLocationRowClass(region, subRegion, city)}`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                              <div className="w-18"></div>
                              <span className="mr-2">{getEntityIcon('location')}</span>
                              <span>{location}</span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                              {locationData.total}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {locationData.positive} ({getPercentage(locationData.positive, locationData.total)}%)
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {locationData.negative} ({getPercentage(locationData.negative, locationData.total)}%)
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {locationData.neutral} ({getPercentage(locationData.neutral, locationData.total)}%)
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                                    style={{ width: `${getPercentage(locationData.positive, locationData.total)}%` }}>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            
            {/* Product-based hierarchy (Amazon, App Store, Play Store) */}
            {viewMode === 'product' && Object.entries(hierarchyData.productHierarchy).map(([category, categoryData]) => (
              <React.Fragment key={`category-${category}`}>
                {/* Category row */}
                <tr 
                  className={`cursor-pointer ${getRowBgColor('category')}`} 
                  onClick={() => toggleProductCategory(category)}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                    <span className="mr-2">{getChevronIcon(expandedProducts[category])}</span>
                    <span className="font-bold">{category}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {categoryData.total}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {categoryData.positive} ({getPercentage(categoryData.positive, categoryData.total)}%)
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {categoryData.negative} ({getPercentage(categoryData.negative, categoryData.total)}%)
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {categoryData.neutral} ({getPercentage(categoryData.neutral, categoryData.total)}%)
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                           style={{ width: `${getPercentage(categoryData.positive, categoryData.total)}%` }}>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Product/App rows */}
                {category === 'Amazon Products' ? (
                  // Amazon Products
                  Object.entries(categoryData.products).map(([product, productData]) => (
                    <tr 
                      key={`product-${category}-${product}`}
                      className={`${getRowBgColor('product')} ${getProductRowClass(category)}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                        <div className="w-6"></div>
                        <span className="mr-2">{getEntityIcon('product')}</span>
                        <span>{product}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                        {productData.total}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {productData.positive} ({getPercentage(productData.positive, productData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {productData.negative} ({getPercentage(productData.negative, productData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {productData.neutral} ({getPercentage(productData.neutral, productData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                               style={{ width: `${getPercentage(productData.positive, productData.total)}%` }}>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  // Mobile Apps
                  Object.entries(categoryData.apps).map(([app, appData]) => (
                    <tr 
                      key={`app-${category}-${app}`}
                      className={`${getRowBgColor('product')} ${getProductRowClass(category)}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                        <div className="w-6"></div>
                        <span className="mr-2">{getEntityIcon('app')}</span>
                        <span>{app}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                        {appData.total}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {appData.positive} ({getPercentage(appData.positive, appData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {appData.negative} ({getPercentage(appData.negative, appData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {appData.neutral} ({getPercentage(appData.neutral, appData.total)}%)
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-40">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-red-500 via-yellow-400 to-green-500" 
                               style={{ width: `${getPercentage(appData.positive, appData.total)}%` }}>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </React.Fragment>
            ))}
            
            {/* No data message */}
            {((viewMode === 'location' && hierarchyData.totalLocationReviews === 0) || 
              (viewMode === 'product' && hierarchyData.totalProductReviews === 0)) && (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No {viewMode === 'location' ? 'location' : 'product'} data available</p>
                    <button 
                      onClick={() => setViewMode(viewMode === 'location' ? 'product' : 'location')}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Switch to {viewMode === 'location' ? 'product' : 'location'} view
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Legend for hierarchy levels */}
      <div className="mt-4 flex flex-wrap gap-4 justify-end text-xs text-gray-500">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-white border border-gray-300 mr-1"></div>
          <span>Region</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-50 border border-gray-300 mr-1"></div>
          <span>Sub-Region</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></div>
          <span>City</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-200 border border-gray-300 mr-1"></div>
          <span>Location</span>
        </div>
      </div>
    </div>
  );
};

export default HierarchyTable;